import { Center, Spinner } from '@chakra-ui/react'
import { useActiveContentContext } from '@src/contexts/ActiveContentContext'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { useUniversalEffect } from '@src/hooks/useUniversalEffect'
import { parseAsString, useQueryState } from 'nuqs'
import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react'

type ChatContextValue = {
  activeParentCommentId?: string
  activeThreadId?: string
  breadcrumbs?: {
    title: string
    href: string
  }[]
  setActiveParentCommentId?: (parentCommentId: string) => void
  setActiveThreadId?: (threadId: string) => void
  setBreadcrumbs?: (breadcrumbs: ChatContextValue['breadcrumbs']) => void
}

export const ChatContext = createContext<ChatContextValue>({})

export const useChatContext = () => useContext(ChatContext)

export const ChatContextProvider: FC<ChatContextValue & { children?: ReactNode }> = props => {
  const { currentUser } = useCurrentUser()
  const activeContent = useActiveContentContext()
  const [activeThreadId, setActiveThreadId] = useQueryState('threadId', parseAsString)
  const [activeParentCommentId, setActiveParentCommentId] = useQueryState('parentCommentId', parseAsString)

  useEffect(() => {
    if (activeParentCommentId) {
      setActiveParentCommentId(null)
    }
  }, [activeThreadId])

  useUniversalEffect(() => {
    activeContent.setSiteSection('chat')
  })

  const [breadcrumbs, setBreadcrumbs] = useState<ChatContextValue['breadcrumbs']>([
    { title: 'Chat', href: '/inbox' },
  ])

  const contextValue = {
    breadcrumbs,
    setBreadcrumbs,
    activeParentCommentId,
    activeThreadId,
    setActiveParentCommentId,
    setActiveThreadId,
  }

  if (!currentUser) {
    return (
      <Center padding={'1em'}>
        <Spinner />
      </Center>
    )
  }

  return (
    <ChatContext.Provider key={currentUser.userId} value={contextValue}>
      {props.children}
    </ChatContext.Provider>
  )
}
